import React, { useEffect, useState } from "react";
import { api, asList } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import VisitorsHeader from "@/pages/Visitors/Header";
import VisitorsTabs from "@/pages/Visitors/Tabs";
import VisitorList from "@/pages/Visitors/VisitorList";
import AppointmentList from "@/pages/Visitors/AppointmentList";
import CheckInDialog from "@/pages/Visitors/CheckInDialog";
import AppointmentDialog from "@/pages/Visitors/AppointmentDialog";

/**
 * Visitor Management page — Watchman uses this at the gate on a phone/tablet.
 * Managers/Admins see the full log + reports. Any staff can create appointments
 * and see their own visitor history.
 *
 * This file is an orchestrator only — every visible piece lives in `./Visitors/`.
 */
export function VisitorsPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState("inside"); // inside | today | log
  const [inside, setInside] = useState([]);
  const [today, setToday] = useState([]);
  const [log, setLog] = useState([]);
  const [openCheckIn, setOpenCheckIn] = useState(false);
  const [openAppt, setOpenAppt] = useState(false);
  const [dash, setDash] = useState(null);

  const isGate = ["admin", "super-admin", "manager", "watchman"].includes(user?.role);

  const load = async () => {
    try {
      const [ins, tdy, all, d] = await Promise.all([
        api.get("/visitors/currently-inside"),
        isGate ? api.get("/visitors/appointments/today") : Promise.resolve({ data: [] }),
        api.get("/visitors"),
        api.get("/visitors/dashboard").catch(() => ({ data: null })),
      ]);
      setInside(asList(ins.data));
      setToday(asList(tdy.data));
      setLog(asList(all.data));
      setDash(d.data);
    } catch (err) { void err; }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  return (
    <div className="space-y-6" data-testid="visitors-page">
      <VisitorsHeader
        user={user}
        isGate={isGate}
        onNewAppointment={() => setOpenAppt(true)}
        onCheckIn={() => setOpenCheckIn(true)}
      />

      {dash && (
        <div className="grid grid-cols-3 gap-3">
          <MiniStat label="Today" value={dash.today}/>
          <MiniStat label="Inside" value={dash.currently_inside} accent/>
          <MiniStat label="Expected" value={dash.expected}/>
        </div>
      )}

      <VisitorsTabs active={tab} onChange={setTab}/>

      {tab === "inside" && (
        <VisitorList rows={inside} onCheckOut={load} showCheckout={isGate} currentUserId={user?.id}/>
      )}
      {tab === "today" && (
        <AppointmentList rows={today} onCheckIn={(a) => setOpenCheckIn(a)}/>
      )}
      {tab === "log" && (
        <VisitorList rows={log} onCheckOut={load} showCheckout={false} currentUserId={user?.id}/>
      )}

      <CheckInDialog
        open={!!openCheckIn}
        appt={typeof openCheckIn === "object" ? openCheckIn : null}
        onClose={() => setOpenCheckIn(false)}
        onDone={() => { setOpenCheckIn(false); load(); }}
      />
      <AppointmentDialog
        open={openAppt}
        onClose={() => setOpenAppt(false)}
        onDone={() => { setOpenAppt(false); load(); }}
      />
    </div>
  );
}

function MiniStat({ label, value, accent }) {
  return (
    <div className={`rounded-xl border p-3 ${accent ? "bg-[#F6EED8] border-[#C9A24B]/40" : "bg-white border-[#E4DFCF]"}`}>
      <div className="text-[10px] uppercase tracking-widest text-[#4A5A50]">{label}</div>
      <div className="text-lg font-medium mt-0.5">{value ?? "—"}</div>
    </div>
  );
}
